import React, {
  createContext,
  useContext,
  useCallback,
  useState,
} from 'react';

export type RulerPoints = [number | null, number | null];

interface ChartRulerContextType {
  isRulerActive: boolean;
  rulerPoints: RulerPoints;
  activateRuler: () => void;
  addRulerPoint: (timestamp: number) => void;
  clearRuler: () => void;
}

const ChartRulerContext = createContext<ChartRulerContextType | null>(null);

export const useChartRulerContext = () => useContext(ChartRulerContext);

interface ChartRulerProviderProps {
  children: React.ReactNode;
}

export const ChartRulerProvider: React.FC<ChartRulerProviderProps> = ({
  children,
}) => {
  const [isRulerActive, setIsRulerActive] = useState(false);
  const [rulerPoints, setRulerPoints] = useState<RulerPoints>([null, null]);

  const activateRuler = useCallback(() => {
    setIsRulerActive((prev) => {
      setRulerPoints([null, null]);
      return !prev;
    });
  }, []);

  const addRulerPoint = useCallback((timestamp: number) => {
    setRulerPoints((prev) => {
      if (prev[0] === null) {
        return [timestamp, null];
      }
      return [prev[0], timestamp];
    });
  }, []);

  const clearRuler = useCallback(() => {
    setRulerPoints([null, null]);
    setIsRulerActive(false);
  }, []);

  const value: ChartRulerContextType = {
    isRulerActive,
    rulerPoints,
    activateRuler,
    addRulerPoint,
    clearRuler,
  };

  return (
    <ChartRulerContext.Provider value={value}>
      {children}
    </ChartRulerContext.Provider>
  );
};
